import { useState, useEffect, useRef, useCallback } from 'react'
import * as XLSX from 'xlsx'
import templateService, { ObjectiveTemplate, TemplateStatsResponse } from '@/services/api/templateService'
import { apiClient } from '@/services/apiClient'
import toast from 'react-hot-toast'
import {
  ViewMode,
  ImportPreviewData,
  KPA_LOOKUP,
  processImportData,
} from '@/utils/templateImportUtils'
// Strategic framework lookups are used at allocation time, not during template import.
// Templates store names/codes only — IDs are resolved at runtime from API lookups.

export function useTemplateManagement() {
  // View and tab state
  const [activeTab, setActiveTab] = useState<'objectives' | 'templates' | 'pending' | 'analytics'>('objectives')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')

  // Data state
  const [templates, setTemplates] = useState<ObjectiveTemplate[]>([])
  const [objectives, setObjectives] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  // Search and filter state (for templates)
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [showPublishedOnly, setShowPublishedOnly] = useState(false)

  // Objective-specific filters
  const [selectedObjKPAs, setSelectedObjKPAs] = useState<string[]>([])
  const [selectedObjPillars, setSelectedObjPillars] = useState<string[]>([])
  const [selectedObjStatuses, setSelectedObjStatuses] = useState<string[]>([])
  const [selectedObjRoles, setSelectedObjRoles] = useState<string[]>([])
  const [showLibraryOnly, setShowLibraryOnly] = useState(false)

  // Selection and bulk operations
  const [selectedTemplates, setSelectedTemplates] = useState<Set<number>>(new Set())
  const [previewTemplate, setPreviewTemplate] = useState<ObjectiveTemplate | null>(null)
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)

  // Approval workflow
  const [approvalTemplate, setApprovalTemplate] = useState<ObjectiveTemplate | null>(null)
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false)
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve')
  const [approvalNotes, setApprovalNotes] = useState('')
  const [publishImmediately, setPublishImmediately] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Stats
  const [stats, setStats] = useState<TemplateStatsResponse>({
    total_templates: 0,
    approved_templates: 0,
    pending_approval: 0,
    published_templates: 0,
    most_used_template: null,
    most_used_count: 0
  })

  // Template creation/edit dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ObjectiveTemplate | null>(null)
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    template_type: 'role_based',
    role_type: '',
    objectives: [] as any[]
  })

  // Track original library IDs for update detection
  const [objectiveLibraryMap, setObjectiveLibraryMap] = useState<Map<number, string>>(new Map())

  // Template dialog filters
  const [templateDialogSearch, setTemplateDialogSearch] = useState('')
  const [templateDialogKpaFilter, setTemplateDialogKpaFilter] = useState<string>('all')
  const [templateDialogPillarFilter, setTemplateDialogPillarFilter] = useState<string>('all')
  const [templateDialogStatusFilter, setTemplateDialogStatusFilter] = useState<string>('all')

  // Available objectives from database
  const [availableObjectives, setAvailableObjectives] = useState<any[]>([])
  const [loadingObjectives, setLoadingObjectives] = useState(false)

  // Objective CRUD operations
  const [selectedObjective, setSelectedObjective] = useState<any | null>(null)
  const [objectiveDialogOpen, setObjectiveDialogOpen] = useState(false)
  const [objectiveDialogMode, setObjectiveDialogMode] = useState<'view' | 'edit'>('view')
  const [showObjectiveWizard, setShowObjectiveWizard] = useState(false)

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning' as 'danger' | 'warning' | 'info' | 'success',
    confirmText: 'Confirm',
    onConfirm: () => {}
  })

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<ImportPreviewData | null>(null)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 })
  const [importResults, setImportResults] = useState<{ success: number; failed: number; errors: string[] }>({ success: 0, failed: 0, errors: [] })
  const [updateExistingTemplates, setUpdateExistingTemplates] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadData()
  }, [activeTab])

  const loadData = async () => {
    setLoading(true)
    try {
      if (activeTab === 'objectives') {
        const response = await apiClient.get<any[]>('/api/v1/objectives/individual/library?limit=100&expand=kpa,pillar,strategic_objective')
        setObjectives(response.data || [])
      } else {
        let response
        if (activeTab === 'pending') {
          response = await templateService.getPendingTemplates({ limit: 100 })
        } else if (activeTab === 'templates') {
          response = await templateService.listTemplates({ limit: 100 })
        }

        if (response) {
          setTemplates(response.items)
        }
      }

      // Load stats
      const statsData = await templateService.getStats()
      setStats(statsData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleLibraryObjective = async (objectiveId: number, currentStatus: boolean) => {
    try {
      if (import.meta.env.DEV) console.log('Toggling library status for objective:', objectiveId, 'Current status:', currentStatus, 'New status:', !currentStatus)
      const response = await apiClient.put(`/api/v1/objectives/individual/${objectiveId}/library-status`, { is_library: !currentStatus })

      if (response.status === 200) {
        toast.success(currentStatus ? 'Removed from library' : 'Added to library')
        loadData()
      } else {
        throw new Error('Failed to update objective')
      }
    } catch (error) {
      console.error('Error toggling library status:', error)
      toast.error('Failed to update objective')
    }
  }

  const handleApproveClick = (template: ObjectiveTemplate) => {
    setApprovalTemplate(template)
    setApprovalAction('approve')
    setApprovalNotes('')
    setPublishImmediately(false)
    setApprovalDialogOpen(true)
  }

  const handleRejectClick = (template: ObjectiveTemplate) => {
    setApprovalTemplate(template)
    setApprovalAction('reject')
    setApprovalNotes('')
    setApprovalDialogOpen(true)
  }

  const handleApprovalSubmit = async () => {
    if (!approvalTemplate) return

    if (approvalAction === 'reject' && !approvalNotes.trim()) {
      toast.error('Rejection reason is required')
      return
    }

    setProcessing(true)
    try {
      if (approvalAction === 'approve') {
        await templateService.approveTemplate(approvalTemplate.id, {
          notes: approvalNotes,
          publish_immediately: publishImmediately
        })
        toast.success(`Template approved${publishImmediately ? ' and published' : ''}!`)
      } else {
        await templateService.rejectTemplate(approvalTemplate.id, {
          notes: approvalNotes
        })
        toast.success('Template rejected')
      }

      setApprovalDialogOpen(false)
      loadData()
    } catch (error: any) {
      console.error('Error processing approval:', error)
      toast.error(error.response?.data?.detail || 'Failed to process approval')
    } finally {
      setProcessing(false)
    }
  }

  const handlePublishToggle = async (template: ObjectiveTemplate) => {
    try {
      if (template.is_published) {
        await templateService.unpublishTemplate(template.id)
        toast.success('Template unpublished')
      } else {
        await templateService.publishTemplate(template.id)
        toast.success('Template published')
      }
      loadData()
    } catch (error: any) {
      console.error('Error toggling publish status:', error)
      toast.error(error.response?.data?.detail || 'Failed to update template')
    }
  }

  const handleDeleteTemplate = (templateId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await templateService.deleteTemplate(templateId)
          toast.success('Template deleted successfully')
          loadData()
        } catch (error) {
          console.error('Error deleting template:', error)
          toast.error('Failed to delete template')
        }
      }
    })
  }

  const handleCreateTemplate = async () => {
    setEditingTemplate(null)
    setTemplateFormData({
      name: '',
      description: '',
      template_type: 'role_based',
      role_type: '',
      objectives: []
    })
    resetTemplateDialogFilters()
    setTemplateDialogOpen(true)

    // Load available objectives if not already loaded
    if (availableObjectives.length === 0) {
      await loadObjectives()
    }
  }

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false)
    resetTemplateDialogFilters()
  }

  const resetTemplateDialogFilters = () => {
    setTemplateDialogSearch('')
    setTemplateDialogKpaFilter('all')
    setTemplateDialogPillarFilter('all')
    setTemplateDialogStatusFilter('all')
  }

  const loadObjectives = async () => {
    setLoadingObjectives(true)
    try {
      const response = await apiClient.get<any[]>('/api/v1/objectives/individual/library?library_only=true&limit=100&expand=kpa,pillar,strategic_objective')
      setAvailableObjectives(response.data || [])
    } catch (error) {
      console.error('Error loading objectives:', error)
      toast.error('Failed to load objectives')
    } finally {
      setLoadingObjectives(false)
    }
  }

  // Objective CRUD handlers
  const handleViewObjective = (objective: any) => {
    setSelectedObjective(objective)
    setObjectiveDialogMode('view')
    setObjectiveDialogOpen(true)
  }

  const handleEditObjective = (objective: any) => {
    setSelectedObjective(objective)
    setObjectiveDialogMode('edit')
    setObjectiveDialogOpen(true)
  }

  const handleCreateObjective = () => {
    setShowObjectiveWizard(true)
  }

  const handleUpdateObjective = async (objectiveId: number, updates: any) => {
    try {
      const response = await apiClient.put(`/api/v1/objectives/individual/${objectiveId}/update`, updates)

      if (response.status === 200) {
        toast.success('Objective updated successfully')
        loadData()
        setObjectiveDialogOpen(false)
      } else {
        throw new Error('Failed to update objective')
      }
    } catch (error) {
      console.error('Error updating objective:', error)
      toast.error('Failed to update objective')
    }
  }

  const handleDeleteObjective = (objectiveId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Objective',
      message: 'Are you sure you want to delete this objective? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          const response = await apiClient.delete(`/api/v1/objectives/individual/${objectiveId}`)

          if (response.status === 200) {
            toast.success('Objective deleted successfully')
            loadData()
            setObjectiveDialogOpen(false)
          } else {
            throw new Error('Failed to delete objective')
          }
        } catch (error) {
          console.error('Error deleting objective:', error)
          toast.error('Failed to delete objective')
        }
      }
    })
  }

  const handleObjectiveCreated = async () => {
    setShowObjectiveWizard(false)
    toast.success('Objective created successfully')
    loadData()
  }

  const handleSaveTemplate = async () => {
    if (!templateFormData.name.trim()) {
      toast.error('Template name is required')
      return
    }

    if (templateFormData.objectives.length === 0) {
      toast.error('Please add at least one objective to the template')
      return
    }

    setProcessing(true)
    try {
      // Strip internal tracking fields before saving
      const cleanObjectives = templateFormData.objectives.map(obj => {
        const { _tempId, _sourceLibraryId, _snapshotDate, _wasUpdated, ...cleanObj } = obj
        return cleanObj
      })

      const templateData = {
        name: templateFormData.name,
        description: templateFormData.description,
        template_type: templateFormData.template_type,
        role_type: templateFormData.role_type || undefined,
        objectives_data: {
          objectives: cleanObjectives
        }
      }

      if (editingTemplate) {
        // Update existing template
        await templateService.updateTemplate(editingTemplate.id, templateData)
        toast.success('Template updated successfully!')
      } else {
        // Create new template
        await templateService.createTemplate(templateData)
        toast.success('Template created successfully!')
      }

      handleCloseTemplateDialog()
      loadData()
    } catch (error: any) {
      console.error('Error saving template:', error)
      toast.error(error.response?.data?.detail || 'Failed to save template')
    } finally {
      setProcessing(false)
    }
  }

  const handleEditTemplate = async (template: ObjectiveTemplate) => {
    // Parse objectives from template data
    const objectivesData = template.objectives_data as any
    const objectives = objectivesData?.objectives || []

    setEditingTemplate(template)
    setTemplateFormData({
      name: template.name,
      description: template.description || '',
      template_type: template.template_type || 'role_based',
      role_type: template.role_type || '',
      objectives: objectives
    })

    resetTemplateDialogFilters()
    setTemplateDialogOpen(true)

    // Load available objectives if not already loaded
    if (availableObjectives.length === 0) {
      await loadObjectives()
    }
  }

  const handleSubmitForApproval = async (templateId: number) => {
    try {
      await templateService.submitForApproval(templateId)
      toast.success('Template submitted for approval')
      loadData()
    } catch (error: any) {
      console.error('Error submitting template:', error)
      toast.error(error.response?.data?.detail || 'Failed to submit template')
    }
  }

  const handlePublishDraft = (templateId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Publish Template Directly',
      message: 'Are you sure you want to publish this template directly? It will skip the approval process and be immediately available for use.',
      type: 'warning',
      confirmText: 'Publish Now',
      onConfirm: async () => {
        try {
          // First approve the template
          await templateService.approveTemplate(templateId, {
            notes: 'Direct publish',
            publish_immediately: true
          })
          toast.success('Template published successfully!')
          loadData()
        } catch (error: any) {
          console.error('Error publishing template:', error)
          toast.error(error.response?.data?.detail || 'Failed to publish template')
        }
      }
    })
  }

  const handleAddObjectiveToTemplate = (objective: any) => {
    // Create a snapshot of the objective
    const tempId = Date.now()
    const objectiveSnapshot = {
      _tempId: tempId,
      _sourceLibraryId: objective.id,
      _snapshotDate: new Date().toISOString(),
      library_objective_id: objective.id,
      title: objective.title,
      description: objective.description,
      role_type: 'template',
      functional_area: objective.functional_area,
      status: 'template',
      progress: 0,
      start_date: objective.start_date,
      end_date: objective.end_date,
      kpa_id: objective.kpa_id,
      pillar_id: objective.pillar_id,
      strategic_objective_id: objective.strategic_objective_id,
      is_library_objective: false,
      source_type: 'template',
      kpis: (objective.kpis || []).map((kpi: any) => ({
        name: kpi.name,
        description: kpi.description,
        unit: kpi.unit,
        target_value: kpi.target_value,
        current_value: 0,
        frequency: kpi.frequency,
        status: kpi.status || 'on-track'
      })),
      kpa: objective.kpa,
      pillar: objective.pillar,
      strategic_objective: objective.strategic_objective
    }

    setTemplateFormData(prev => ({
      ...prev,
      objectives: [...prev.objectives, objectiveSnapshot]
    }))
    toast.success('Objective snapshot added to template')
  }

  const handleRemoveObjectiveFromTemplate = (index: number) => {
    setTemplateFormData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }))
  }

  const handleUpdateObjectiveFromLibrary = async (index: number) => {
    const currentObj = templateFormData.objectives[index]
    const sourceLibraryId = currentObj.library_objective_id || currentObj._sourceLibraryId || currentObj.id

    if (!sourceLibraryId) {
      toast.error('No source library objective found for this item')
      return
    }

    // Find the latest version in available objectives
    const latestVersion = availableObjectives.find(obj => obj.id === sourceLibraryId)

    if (!latestVersion) {
      toast.error('Source objective no longer exists in library')
      return
    }

    // Create updated snapshot
    const tempId = currentObj._tempId || Date.now()
    const updatedSnapshot = {
      _tempId: tempId,
      _sourceLibraryId: sourceLibraryId,
      _snapshotDate: new Date().toISOString(),
      _wasUpdated: true,
      library_objective_id: sourceLibraryId,
      title: latestVersion.title,
      description: latestVersion.description,
      role_type: 'template',
      functional_area: latestVersion.functional_area,
      status: 'template',
      progress: 0,
      start_date: latestVersion.start_date,
      end_date: latestVersion.end_date,
      kpa_id: latestVersion.kpa_id,
      pillar_id: latestVersion.pillar_id,
      strategic_objective_id: latestVersion.strategic_objective_id,
      is_library_objective: false,
      source_type: 'template',
      kpis: (latestVersion.kpis || []).map((kpi: any) => ({
        name: kpi.name,
        description: kpi.description,
        unit: kpi.unit,
        target_value: kpi.target_value,
        current_value: 0,
        frequency: kpi.frequency,
        status: kpi.status || 'on-track'
      })),
      kpa: latestVersion.kpa,
      pillar: latestVersion.pillar,
      strategic_objective: latestVersion.strategic_objective
    }

    setTemplateFormData(prev => ({
      ...prev,
      objectives: prev.objectives.map((obj, i) => i === index ? updatedSnapshot : obj)
    }))

    toast.success('Objective updated from library')
  }

  const handleRefreshAllFromLibrary = async () => {
    if (availableObjectives.length === 0) {
      await loadObjectives()
    }

    let updatedCount = 0
    const updatedObjectives = await Promise.all(
      templateFormData.objectives.map(async (obj) => {
        const sourceLibraryId = obj.library_objective_id || obj._sourceLibraryId || obj.id
        if (!sourceLibraryId) return obj

        const latestVersion = availableObjectives.find(libObj => libObj.id === sourceLibraryId)
        if (!latestVersion) return obj

        updatedCount++
        const tempId = obj._tempId || Date.now()
        return {
          _tempId: tempId,
          _sourceLibraryId: sourceLibraryId,
          _snapshotDate: new Date().toISOString(),
          _wasUpdated: true,
          library_objective_id: sourceLibraryId,
          title: latestVersion.title,
          description: latestVersion.description,
          role_type: 'template',
          functional_area: latestVersion.functional_area,
          status: 'template',
          progress: 0,
          start_date: latestVersion.start_date,
          end_date: latestVersion.end_date,
          kpa_id: latestVersion.kpa_id,
          pillar_id: latestVersion.pillar_id,
          strategic_objective_id: latestVersion.strategic_objective_id,
          is_library_objective: false,
          source_type: 'template',
          kpis: (latestVersion.kpis || []).map((kpi: any) => ({
            name: kpi.name,
            description: kpi.description,
            unit: kpi.unit,
            target_value: kpi.target_value,
            current_value: 0,
            frequency: kpi.frequency,
            status: kpi.status || 'on-track'
          })),
          kpa: latestVersion.kpa,
          pillar: latestVersion.pillar,
          strategic_objective: latestVersion.strategic_objective
        }
      })
    )

    setTemplateFormData(prev => ({
      ...prev,
      objectives: updatedObjectives
    }))

    toast.success(`${updatedCount} objective(s) refreshed from library`)
  }

  const handlePreviewTemplate = (template: ObjectiveTemplate) => {
    setPreviewTemplate(template)
    setPreviewDialogOpen(true)
  }

  // Filter templates based on search and filters
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(t.template_type)
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(t.status)
    const matchesRole = selectedRoles.length === 0 || (t.role_type && selectedRoles.includes(t.role_type))
    const matchesPublished = !showPublishedOnly || t.is_published

    return matchesSearch && matchesType && matchesStatus && matchesRole && matchesPublished
  })

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTemplates(new Set(filteredTemplates.map(t => t.id)))
    } else {
      setSelectedTemplates(new Set())
    }
  }

  const handleSelectTemplate = (templateId: number, checked: boolean) => {
    const newSelection = new Set(selectedTemplates)
    if (checked) {
      newSelection.add(templateId)
    } else {
      newSelection.delete(templateId)
    }
    setSelectedTemplates(newSelection)
  }

  // Import handlers
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true)
    setImportStep('upload')
    setImportFile(null)
    setImportPreview(null)
    setImportProgress({ current: 0, total: 0 })
    setImportResults({ success: 0, failed: 0, errors: [] })
  }

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false)
    setImportStep('upload')
    setImportFile(null)
    setImportPreview(null)
  }

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file) return

    // Validate file type - only support Excel
    const validTypes = ['.xlsx']
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload an Excel file (.xlsx)')
      return
    }

    setImportFile(file)

    try {
      let rows: string[][] = []

      // Parse Excel file using xlsx library
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })

      // Get the first sheet (template data)
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]

      // Convert to array of arrays
      const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1, defval: '' })
      rows = jsonData.map(row => row.map(cell => String(cell ?? '')))

      if (rows.length < 2) {
        toast.error('File must contain at least a header row and one data row')
        return
      }

      const headers = rows[0].map(h => String(h).toLowerCase().trim())

      // Validate required headers - support both formats
      const hasCodeFormat = headers.includes('l1_strategic_objective_code') || headers.includes('kpa_code')
      const requiredHeaders = hasCodeFormat
        ? ['template_name', 'objective_title', 'kpa_code']
        : ['template_name', 'objective_title', 'kpa_name']
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))

      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`)
        return
      }

      // Get column indices for required fields (used to filter real data rows)
      const templateNameIdx = headers.indexOf('template_name')
      const objectiveTitleIdx = headers.indexOf('objective_title')

      // Filter rows: must have BOTH template_name AND objective_title populated
      const dataRows = rows.slice(1).filter(row => {
        const hasTemplateName = templateNameIdx >= 0 && row[templateNameIdx]?.trim()
        const hasObjectiveTitle = objectiveTitleIdx >= 0 && row[objectiveTitleIdx]?.trim()
        return hasTemplateName && hasObjectiveTitle
      })

      // Process and preview data
      const preview = processImportData(dataRows, headers)
      setImportPreview(preview)
      setImportStep('preview')

    } catch (error) {
      console.error('Error parsing file:', error)
      toast.error('Failed to parse file. Please check the format.')
    }
  }, [])

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDownloadTemplate = () => {
    window.open('/templates/objective_template_import.xlsx', '_blank')
  }

  const handleImportTemplates = async () => {
    if (!importPreview || importPreview.templates.length === 0) return

    setImportStep('importing')
    setImportProgress({ current: 0, total: importPreview.templates.length })

    const results = { success: 0, failed: 0, skipped: 0, errors: [] as string[] }

    // Fetch existing templates and objectives for duplicate detection
    let existingTemplateNames: Set<string> = new Set()
    const existingTemplateMap: Map<string, number> = new Map()
    let existingObjectiveTitles: Set<string> = new Set()

    if (templates && templates.length > 0) {
      existingTemplateNames = new Set(templates.map((t: any) => t.name.toLowerCase().trim()))
      templates.forEach((t: any) => existingTemplateMap.set(t.name.toLowerCase().trim(), t.id))
      console.log('Existing template names for duplicate check:', Array.from(existingTemplateNames))
    }

    try {
      const existingObjectivesResponse = await apiClient.get('/api/v1/objectives/individual/library?limit=1000')
      const existingObjectivesData = Array.isArray(existingObjectivesResponse.data) ? existingObjectivesResponse.data : []
      const activeObjectives = existingObjectivesData.filter((o: any) =>
        o.status !== 'rejected' && o.status !== 'deleted'
      )
      existingObjectiveTitles = new Set(activeObjectives.map((o: any) => o.title?.toLowerCase().trim()).filter(Boolean))
      console.log('Existing objective titles for duplicate check (excluding deleted):', Array.from(existingObjectiveTitles))
    } catch (error) {
      console.error('Error fetching existing objectives for duplicate detection:', error)
    }

    console.log('Import preview templates:', importPreview.templates.map(t => t.name))

    for (let i = 0; i < importPreview.templates.length; i++) {
      const template = importPreview.templates[i]
      setImportProgress({ current: i + 1, total: importPreview.templates.length })

      // Check for duplicate template
      const templateNameLower = template.name.toLowerCase().trim()
      console.log(`Checking template "${template.name}" (normalized: "${templateNameLower}") against existing:`, existingTemplateNames.has(templateNameLower))

      const existingTemplateId = existingTemplateMap.get(templateNameLower)
      if (existingTemplateNames.has(templateNameLower) && !updateExistingTemplates) {
        console.log(`SKIPPING duplicate template: ${template.name}`)
        results.skipped++
        results.errors.push(`${template.name}: Skipped - template already exists`)
        continue
      }

      try {
        // Transform imported data to API format
        const objectives = template.objectives.map(obj => {
          // Resolve kpa_id from kpa_code
          let kpaId: number | undefined
          if (obj.kpa_code && KPA_LOOKUP[obj.kpa_code]) {
            kpaId = KPA_LOOKUP[obj.kpa_code].id
          }

          // Note: IDs are resolved at allocation time from names/codes using API lookups.
          // Templates only store names and codes — NOT database IDs (which are environment-specific).

          return {
            title: obj.title,
            description: obj.description,
            kpa_id: kpaId,
            kpa_code: obj.kpa_code,
            kpa_name: obj.kpa_name,
            pillar_name: obj.pillar_name,
            strategic_objective_name: obj.strategic_objective_name,
            l3_operational_initiative_name: obj.l3_operational_initiative_name,
            l1_strategic_objective_code: obj.l1_strategic_objective_code,
            l2_strategic_initiative_code: obj.l2_strategic_initiative_code,
            l3_operational_initiative_code: obj.l3_operational_initiative_code,
            // Note: IDs are NOT stored in template — they're environment-specific.
            // Allocation code resolves from names/codes at runtime using API lookups.
            start_date: obj.start_date,
            end_date: obj.end_date,
            status: 'template',
            role_type: 'template',
            is_library_objective: false,
            source_type: 'template',
            kpis: obj.kpis.map(kpi => ({
              name: kpi.name,
              description: kpi.description,
              unit: kpi.unit,
              target_value: parseFloat(kpi.target_value) || 0,
              current_value: 0,
              frequency: kpi.frequency,
              status: 'on-track'
            }))
          }
        })

        const templateData = {
          name: template.name,
          description: template.description,
          template_type: template.template_type || 'role_based',
          role_type: template.role_type || undefined,
          objectives_data: { objectives }
        }

        if (existingTemplateId && updateExistingTemplates) {
          // Update existing template with new objectives_data (includes L3 + codes)
          await templateService.updateTemplate(existingTemplateId, {
            objectives_data: templateData.objectives_data
          })
          console.log(`UPDATED existing template: ${template.name} (id: ${existingTemplateId})`)
        } else {
          await templateService.createTemplate(templateData)
        }

        // Also create objectives in the database for future library use (skip duplicates)
        for (const obj of template.objectives) {
          // Check for duplicate objective
          if (existingObjectiveTitles.has(obj.title.toLowerCase().trim())) {
            console.log(`Skipping duplicate objective: ${obj.title}`)
            continue
          }

          try {
            // Map KPA code to kpa_name if available
            const kpaName = obj.kpa_code || obj.kpa_name || 'operating-financial'

            const objectiveData = {
              title: obj.title,
              description: obj.description || '',
              kpa_name: kpaName,
              kpa_display_name: obj.kpa_name,
              pillar_name: obj.pillar_name,
              start_date: obj.start_date,
              end_date: obj.end_date,
              is_library_objective: false,
              kpis: obj.kpis.map(kpi => ({
                name: kpi.name,
                description: kpi.description || '',
                unit: kpi.unit,
                target: kpi.target_value,
                frequency: kpi.frequency
              }))
            }

            await apiClient.post('/api/v1/objectives/individual/', objectiveData)
            // Add to set to prevent duplicates within same import batch
            existingObjectiveTitles.add(obj.title.toLowerCase().trim())
          } catch (objError) {
            // Log but don't fail the whole import if individual objective creation fails
            console.warn(`Could not create objective "${obj.title}" in database:`, objError)
          }
        }

        // Add to set to prevent duplicates within same import batch
        existingTemplateNames.add(template.name.toLowerCase().trim())
        results.success++

      } catch (error: any) {
        console.error(`Failed to import template ${template.name}:`, error)
        results.failed++
        results.errors.push(`${template.name}: ${error.response?.data?.detail || error.message || 'Unknown error'}`)
      }
    }

    setImportResults(results)
    setImportStep('complete')

    // Refresh data if any templates were imported
    if (results.success > 0) {
      loadData()
    }
  }

  const handleBulkDelete = () => {
    if (selectedTemplates.size === 0) return

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Multiple Templates',
      message: `Are you sure you want to delete ${selectedTemplates.size} template${selectedTemplates.size > 1 ? 's' : ''}? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete All',
      onConfirm: async () => {
        try {
          await Promise.all(
            Array.from(selectedTemplates).map(id => templateService.deleteTemplate(id))
          )
          toast.success(`${selectedTemplates.size} templates deleted`)
          setSelectedTemplates(new Set())
          loadData()
        } catch (error) {
          toast.error('Failed to delete some templates')
        }
      }
    })
  }

  // Filter objectives based on search and filters
  const filteredObjectives = objectives.filter(obj => {
    const matchesSearch = !searchQuery ||
      obj.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obj.description?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesKPA = selectedObjKPAs.length === 0 || (obj.kpa?.display_name && selectedObjKPAs.includes(obj.kpa.display_name))
    const matchesPillar = selectedObjPillars.length === 0 || (obj.pillar?.name && selectedObjPillars.includes(obj.pillar.name))
    const matchesStatus = selectedObjStatuses.length === 0 || (obj.status && selectedObjStatuses.includes(obj.status))
    const matchesRole = selectedObjRoles.length === 0 || (obj.role_type && selectedObjRoles.includes(obj.role_type))
    const matchesLibrary = !showLibraryOnly || obj.is_library_objective

    return matchesSearch && matchesKPA && matchesPillar && matchesStatus && matchesRole && matchesLibrary
  })

  const clearFilters = () => {
    if (activeTab === 'objectives') {
      setSelectedObjKPAs([])
      setSelectedObjPillars([])
      setSelectedObjStatuses([])
      setSelectedObjRoles([])
      setShowLibraryOnly(false)
    } else {
      setSelectedTypes([])
      setSelectedStatuses([])
      setSelectedRoles([])
      setShowPublishedOnly(false)
    }
    setSearchQuery('')
  }

  const activeFiltersCount = activeTab === 'objectives'
    ? selectedObjKPAs.length + selectedObjPillars.length + selectedObjStatuses.length + selectedObjRoles.length + (showLibraryOnly ? 1 : 0)
    : selectedTypes.length + selectedStatuses.length + selectedRoles.length + (showPublishedOnly ? 1 : 0)

  // Get unique values for template filters
  const availableTypes = Array.from(new Set(templates.map(t => t.template_type))).sort()
  const availableStatuses = Array.from(new Set(templates.map(t => t.status))).sort()
  const availableRoles = Array.from(new Set(templates.map(t => t.role_type).filter(Boolean) as string[])).sort()

  // Get unique values for objective filters
  const availableObjKPAs = Array.from(new Set(objectives.map(obj => obj.kpa?.display_name).filter(Boolean) as string[])).sort()
  const availableObjPillars = Array.from(new Set(objectives.map(obj => obj.pillar?.name).filter(Boolean) as string[])).sort()
  const availableObjStatuses = Array.from(new Set(objectives.map(obj => obj.status).filter(Boolean) as string[])).sort()
  const availableObjRoles = Array.from(new Set(objectives.map(obj => obj.role_type).filter(Boolean) as string[])).sort()

  return {
    // View state
    activeTab, setActiveTab,
    viewMode, setViewMode,

    // Data
    templates, objectives, loading, stats,
    filteredTemplates, filteredObjectives,

    // Search and filters
    searchQuery, setSearchQuery,
    showFilters, setShowFilters,
    selectedTypes, setSelectedTypes,
    selectedStatuses, setSelectedStatuses,
    selectedRoles, setSelectedRoles,
    showPublishedOnly, setShowPublishedOnly,
    selectedObjKPAs, setSelectedObjKPAs,
    selectedObjPillars, setSelectedObjPillars,
    selectedObjStatuses, setSelectedObjStatuses,
    selectedObjRoles, setSelectedObjRoles,
    showLibraryOnly, setShowLibraryOnly,
    activeFiltersCount,
    clearFilters,

    // Filter options
    availableTypes, availableStatuses, availableRoles,
    availableObjKPAs, availableObjPillars, availableObjStatuses, availableObjRoles,

    // Selection
    selectedTemplates, setSelectedTemplates,
    handleSelectAll, handleSelectTemplate,

    // Preview
    previewTemplate, previewDialogOpen, setPreviewDialogOpen,
    handlePreviewTemplate,

    // Approval
    approvalTemplate, approvalDialogOpen, setApprovalDialogOpen,
    approvalAction, approvalNotes, setApprovalNotes,
    publishImmediately, setPublishImmediately,
    processing,
    handleApproveClick, handleRejectClick, handleApprovalSubmit,

    // Template CRUD
    templateDialogOpen, setTemplateDialogOpen,
    editingTemplate,
    templateFormData, setTemplateFormData,
    handleCreateTemplate, handleCloseTemplateDialog,
    handleSaveTemplate, handleEditTemplate,
    handleDeleteTemplate, handlePublishToggle,
    handleSubmitForApproval, handlePublishDraft,

    // Template dialog filters
    templateDialogSearch, setTemplateDialogSearch,
    templateDialogKpaFilter, setTemplateDialogKpaFilter,
    templateDialogPillarFilter, setTemplateDialogPillarFilter,
    templateDialogStatusFilter, setTemplateDialogStatusFilter,
    resetTemplateDialogFilters,

    // Available objectives
    availableObjectives, loadingObjectives,
    handleAddObjectiveToTemplate,
    handleRemoveObjectiveFromTemplate,
    handleUpdateObjectiveFromLibrary,
    handleRefreshAllFromLibrary,

    // Objective CRUD
    selectedObjective, objectiveDialogOpen, setObjectiveDialogOpen,
    objectiveDialogMode,
    showObjectiveWizard, setShowObjectiveWizard,
    handleViewObjective, handleEditObjective,
    handleCreateObjective, handleDeleteObjective,
    handleUpdateObjective, handleObjectiveCreated,
    handleToggleLibraryObjective,

    // Confirm dialog
    confirmDialog, setConfirmDialog,

    // Import
    importDialogOpen, setImportDialogOpen,
    importStep, setImportStep,
    importFile, importPreview,
    importProgress, importResults,
    fileInputRef,
    handleOpenImportDialog, handleCloseImportDialog,
    handleFileSelect, handleFileDrop, handleFileInputChange,
    handleDownloadTemplate, handleImportTemplates,
    updateExistingTemplates, setUpdateExistingTemplates,

    // Bulk
    handleBulkDelete,

    // Data reload
    loadData,
  }
}
